import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Download } from "lucide-react";
import { KineticLine, Reveal, Overline, CountUp } from "@/components/Kinetic";
import Marquee from "@/components/Marquee";
import { IMAGES, STATS, PRODUCTS, SUPPLY_CHAIN } from "@/data/site";

export default function Home() {
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 700], [0, 160]);
  const base = sessionStorage.getItem("ng_loaded") ? 0 : 1.85;

  const featured = [PRODUCTS[0], PRODUCTS[1], PRODUCTS[3], PRODUCTS[2], PRODUCTS[5], PRODUCTS[4]];

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative flex min-h-[92vh] items-end overflow-hidden" data-testid="home-hero">
        <motion.div
          initial={{ scale: 1.12, opacity: 0.5 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.8, ease: [0.76, 0, 0.24, 1] }}
          className="absolute inset-0"
        >
          <motion.img
            src={IMAGES.hero}
            alt="Golden hour over Indian farmland"
            style={{ y: heroY, scale: 1.1 }}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-forest/70" />
          <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-forest to-transparent" />
        </motion.div>

        <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-20 pt-40 lg:px-10">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: base + 0.15 }}
          >
            <Overline data-testid="hero-overline">Nishmayy Global</Overline>
          </motion.div>

          <h1 className="mt-6 font-serif font-light leading-[0.95] tracking-tight text-cream" data-testid="hero-heading">
            <KineticLine delay={base + 0.3} className="text-6xl md:text-7xl lg:text-8xl">
              Nurturing Global
            </KineticLine>
            <KineticLine delay={base + 0.45} className="text-6xl md:text-7xl lg:text-8xl">
              Nutrition<span className="text-gold">.</span>
            </KineticLine>
          </h1>

          <Reveal delay={base + 0.85} className="mt-8 max-w-xl">
            <p className="text-base font-light leading-relaxed text-sage md:text-lg" data-testid="hero-subtext">
              Premium dry fruit processing and agro-commodity exports — connecting India's finest
              harvests to tables across 93+ countries.
            </p>
          </Reveal>

          <Reveal delay={base + 1.0} className="mt-10 flex flex-wrap items-center gap-5">
            <Link
              to="/products"
              data-testid="hero-explore-button"
              className="group flex items-center gap-3 bg-gold px-8 py-4 text-sm font-semibold tracking-wide text-forest transition-colors duration-300 hover:bg-cream"
            >
              Explore the Portfolio
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-partner-button"
              className="group flex items-center gap-3 border border-cream/30 px-8 py-4 text-sm tracking-wide text-cream transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              Partner With Us
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* STATS */}
      <section className="border-b border-forest-border bg-forest" data-testid="home-stats">
        <div className="mx-auto grid max-w-7xl grid-cols-2 lg:grid-cols-4">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.1} className={`border-forest-border px-8 py-12 ${i % 2 === 1 ? "border-l" : ""} ${i >= 2 ? "border-t lg:border-t-0" : ""} ${i > 0 ? "lg:border-l" : ""}`}>
              <p className="font-serif text-5xl font-light text-gold md:text-6xl">
                <CountUp to={s.value} suffix={s.suffix} />
              </p>
              <p className="mt-3 text-sm font-medium uppercase tracking-[0.15em] text-cream">{s.label}</p>
              <p className="mt-1 text-xs text-sage">{s.sub}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <Marquee />

      {/* PRODUCT TEASER */}
      <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-36" data-testid="home-products">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Reveal><Overline>Our Commodity Portfolio</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-serif text-4xl font-light leading-tight text-cream md:text-5xl lg:text-6xl">
                A Diverse Portfolio.<br />Endless Possibilities.
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/products"
              data-testid="home-products-viewall"
              className="group flex items-center gap-2 text-sm tracking-wide text-gold"
            >
              View all 6 categories
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {featured.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.12} className={i === 0 ? "lg:col-span-2 lg:row-span-2" : ""}>
              <Link
                to="/products"
                data-testid={`home-product-${p.slug}`}
                className={`group relative block overflow-hidden border border-forest-border ${i === 0 ? "h-[420px] lg:h-full lg:min-h-[624px]" : "h-[300px]"}`}
              >
                <img
                  src={p.image}
                  alt={p.name}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-forest via-forest/60 to-forest/5" />
                <div className="absolute inset-x-0 bottom-0 p-6 lg:p-8">
                  <h3 className="font-serif text-2xl font-light text-cream lg:text-3xl">{p.name}</h3>
                  <p className="mt-1 truncate text-xs uppercase tracking-[0.2em] text-gold/90">{p.tagline}</p>
                  <div className="mt-3 flex flex-wrap gap-2 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                    {p.varieties.slice(0, 4).map((v) => (
                      <span key={v} className="border border-cream/20 bg-forest/50 px-2.5 py-1 text-[11px] text-cream/90 backdrop-blur-sm">
                        {v}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SUPPLY CHAIN STRIP */}
      <section className="border-y border-forest-border bg-forest-surface py-28" data-testid="home-supply-chain">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal><Overline>Our Integrated Supply Chain</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 max-w-2xl font-serif text-4xl font-light leading-tight text-cream md:text-5xl">
              End-to-End. Seamless. Quality Assured.
            </h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 gap-px border border-forest-border bg-forest-border sm:grid-cols-3 lg:grid-cols-9">
            {SUPPLY_CHAIN.map((s, i) => (
              <Reveal key={s.step} delay={i * 0.06} className="bg-forest-surface">
                <div className="group h-full px-5 py-8 transition-colors duration-300 hover:bg-forest-light" data-testid={`supply-step-${s.step}`}>
                  <p className="font-serif text-3xl font-light text-gold/50 transition-colors duration-300 group-hover:text-gold">{s.step}</p>
                  <p className="mt-4 text-sm font-semibold tracking-wide text-cream">{s.title}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-10">
            <Link to="/export-logistics" className="group inline-flex items-center gap-2 text-sm tracking-wide text-gold" data-testid="home-supplychain-link">
              Explore our export capabilities
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* QUOTE */}
      <section className="mx-auto max-w-5xl px-6 py-32 text-center lg:py-40" data-testid="home-quote">
        <Reveal>
          <p className="font-serif text-3xl font-light italic leading-snug text-cream md:text-4xl lg:text-5xl">
            "The world needs reliable partners who understand both the land and the markets.
            <span className="text-gold"> That is where we make a difference.</span>"
          </p>
        </Reveal>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden border-t border-forest-border" data-testid="home-cta">
        <img src={IMAGES.dryFruitsWide} alt="Premium dry fruits" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-forest/85" />
        <div className="relative z-10 mx-auto flex max-w-7xl flex-col items-start gap-10 px-6 py-28 lg:flex-row lg:items-center lg:justify-between lg:px-10">
          <div>
            <Reveal><Overline>Let's build a better future together</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-serif text-4xl font-light leading-tight text-cream md:text-5xl lg:text-6xl">
                Sourcing responsibly.<br />Executing reliably. <span className="text-gold">Growing together.</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <div className="flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                data-testid="home-cta-button"
                className="group flex items-center gap-3 bg-gold px-10 py-5 text-sm font-semibold tracking-wide text-forest transition-colors duration-300 hover:bg-cream"
              >
                Start a Conversation
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a
                href="/catalogue/nishmayy-global-catalogue.pdf"
                download
                data-testid="home-catalogue-button"
                className="group flex items-center gap-3 border border-cream/30 px-10 py-5 text-sm tracking-wide text-cream transition-colors duration-300 hover:border-gold hover:text-gold"
              >
                <Download className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
                Download Catalogue
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
